/*
 * image-watchdog.js  (s12, 2026-07-21)
 *
 * THE BUG IT HUNTS: a product tile image occasionally sits EMPTY on the grid --
 * not a broken-image glyph, an empty box -- and a reload does NOT fix it while
 * opening a fresh window DOES. The tiles are 100% native (<img loading=lazy
 * decoding=async>, no theme JS in the path), so the misfire lives in the browser's
 * own lazy/decode/transport layer (leading theory: a native loading=lazy trigger
 * missed after a post-load reflow; CDN serves images over h3/QUIC). Un-reproducible
 * in isolation, so this does two things:
 *
 *   1. LOG it -- turn the Heisenbug into data. When a tile is in view but still not
 *      painted after a grace window, beacon one decisive datum to Umami (existing
 *      RUM) + console: did the request even START (a PerformanceResourceTiming entry
 *      exists?) -> never-fetched implicates the lazy-trigger theory; fetched-but-empty
 *      implicates transport/decode. Plus nav type (reload vs fresh) and protocol.
 *   2. HEAL it -- nudge the stuck image (eager + reassign src; one cache-bust retry on
 *      error). Self-heals the symptom regardless of root cause.
 *
 * COST: effectively zero. IntersectionObserver is passive/native; we observe a handful
 * of tiles once and unobserve on first intersection; the only timer is a single per-tile
 * grace that is CLEARED the moment the image loads. On a healthy page every timer clears
 * and no work runs. No polling, no rAF, no layout reads in the hot path, deferred script.
 *
 * FAIL-OPEN: the whole thing is wrapped so it can never break the page. A watchdog that
 * can crash the yard is worse than no watchdog.
 */
(function () {
  "use strict";
  try {
    var SELECTOR = ".home-tile-img img, .collection-tile-img img";
    var GRACE_MS = 5000;      // after a tile is near-viewport, how long before we call it stuck
    var ROOT_MARGIN = "300px"; // start watching a bit before it scrolls into view
    var MAX_BEACONS = 6;       // never flood Umami if something goes systemically wrong
    var beacons = 0;

    function painted(img) { return img.complete && img.naturalWidth > 0; }

    function navType() {
      try {
        var n = performance.getEntriesByType("navigation")[0];
        return n ? n.type : "unknown";           // 'reload' | 'navigate' | 'back_forward'
      } catch (e) { return "unknown"; }
    }

    function resourceEntry(url) {
      try {
        var list = performance.getEntriesByType("resource");
        for (var i = list.length - 1; i >= 0; i--) if (list[i].name === url) return list[i];
      } catch (e) {}
      return null;
    }

    function beacon(img, phase) {
      if (beacons >= MAX_BEACONS) return;
      beacons++;
      var url = img.currentSrc || img.src || "";
      var e = resourceEntry(url);
      var a = img.closest ? img.closest("a") : null;
      var data = {
        phase: phase,                                   // 'detected' | 'healed' | 'unhealed'
        page: location.pathname,
        product: a ? a.getAttribute("href") : null,
        chosenWidth: (url.split("width=")[1] || "").split("&")[0] || null,
        fetched: e ? true : false,                      // best-effort: did a request record exist?
        proto: e ? e.nextHopProtocol : null,            // h2 / h3 / ...
        durMs: e ? Math.round(e.duration) : null,
        transfer: e ? e.transferSize : null,            // 0 with an entry => failed/empty body
        nav: navType(),                                 // reload vs fresh window is the whole clue
        loading: img.getAttribute("loading")
      };
      try { console.warn("[img-watchdog]", phase, data); } catch (_) {}
      try { if (window.umami && typeof window.umami.track === "function") window.umami.track("img_misfire", data); } catch (_) {}
    }

    function heal(img) {
      // Reliable re-fetch: the stalled image is bound to a srcset candidate, and reassigning
      // src to its OWN value does not restart a load. So drop srcset/sizes and set a concrete,
      // cache-busted src (the already-chosen candidate) -> the URL genuinely changes -> the
      // browser must fetch fresh. Responsive srcset is traded away for this one healed tile,
      // which is a fine price for "a right-enough image beats an empty box." One retry on error.
      var chosen = img.currentSrc || img.getAttribute("src");
      if (!chosen) { beacon(img, "unhealed"); return; }
      var base = img.getAttribute("src");
      var retried = false;
      function bust(u, k) { return u + (u.indexOf("?") > -1 ? "&" : "?") + k + "=" + Date.now(); }
      function onError() {
        if (retried) { beacon(img, "unhealed"); img.removeEventListener("error", onError); return; }
        retried = true;
        img.setAttribute("src", bust(base || chosen, "wd2"));   // last resort: the src-attr candidate
      }
      img.addEventListener("error", onError);
      img.addEventListener("load", function onLoad() {
        beacon(img, "healed");
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
      }, { once: true });
      try { img.loading = "eager"; } catch (_) {}
      try { img.removeAttribute("srcset"); img.removeAttribute("sizes"); } catch (_) {}
      img.setAttribute("src", bust(chosen, "wd"));               // concrete + cache-busted => fresh fetch
    }

    function watch(img) {
      if (painted(img)) return;
      var timer = setTimeout(function () {
        if (painted(img)) return;                    // it loaded during the grace -> all good
        beacon(img, "detected");
        heal(img);
      }, GRACE_MS);
      img.addEventListener("load", function () { clearTimeout(timer); }, { once: true });
    }

    function start() {
      try {
        var imgs = document.querySelectorAll(SELECTOR);
        if (!imgs.length) return;
        window.__imgWatchdog = { version: "s12", watched: 0, beacons: function () { return beacons; } };
        if (!("IntersectionObserver" in window)) {
          for (var i = 0; i < imgs.length; i++) watch(imgs[i]);
          window.__imgWatchdog.watched = imgs.length;
          return;
        }
        var io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { obs.unobserve(en.target); watch(en.target); }
          });
        }, { rootMargin: ROOT_MARGIN });
        var n = 0;
        imgs.forEach(function (img) { if (!painted(img)) { io.observe(img); n++; } });
        window.__imgWatchdog.watched = n;
      } catch (e) { try { console.warn("[img-watchdog] start failed", e); } catch (_) {} }
    }

    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
    else start();
  } catch (e) { /* fail-open: never break the page */ }
})();
